import { useState, useEffect } from "react";
import { supabase } from "../../supabase";
import ConfirmModal from "../ConfirmModal";

const ROLES = ["architect", "contractor", "client"];
const ROLE_LABELS = { architect: "Architecte", contractor: "Entreprise", client: "Client" };
const ROLE_COLORS = {
  architect:  { bg: "#7EB8C918", border: "#7EB8C940", text: "#7EB8C9", icon: "▲" },
  contractor: { bg: "#A8C97E18", border: "#A8C97E40", text: "#A8C97E", icon: "●" },
  client:     { bg: "#C9A96E18", border: "#C9A96E40", text: "#C9A96E", icon: "◆" },
};

function RoleBadge({ role }) {
  const c = ROLE_COLORS[role] || { bg: "#25242018", border: "#2A2924", text: "#7A7468", icon: "?" };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 4, background: c.bg, border: `1px solid ${c.border}`, color: c.text }}>
      {c.icon} {ROLE_LABELS[role] || role}
    </span>
  );
}

function StatusBadge({ active }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 4, background: active ? "#A8C97E18" : "#FC818118", border: `1px solid ${active ? "#A8C97E40" : "#FC818140"}`, color: active ? "#A8C97E" : "#FC8181" }}>
      {active ? "Actif" : "Inactif"}
    </span>
  );
}

function RoleDropdown({ currentRole, open, onToggle, onSelect }) {
  return (
    <div style={{ position: "relative" }}>
      <button
        className="btn"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #2A2924", fontSize: 11, color: "#E8E2D8", background: "#252420", fontWeight: 500 }}
      >
        Rôle ▾
      </button>
      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, background: "#1A1916", border: "1px solid #2A2924", borderRadius: 8, zIndex: 200, minWidth: 140, boxShadow: "0 8px 24px #00000060", overflow: "hidden" }}
        >
          {ROLES.map(role => (
            <button
              key={role}
              className="btn"
              onClick={() => onSelect(role)}
              style={{ display: "block", width: "100%", padding: "9px 14px", textAlign: "left", fontSize: 13, color: role === currentRole ? "#C9A96E" : "#E8E2D8", background: "none" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#252420"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
            >
              {ROLE_LABELS[role]}{role === currentRole ? " ✓" : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [projectCounts, setProjectCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [openRoleId, setOpenRoleId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  useEffect(() => {
    async function fetchData() {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, name, email, role, is_active, created_at")
        .order("created_at", { ascending: false });

      if (!profiles || profiles.length === 0) { setLoading(false); return; }

      const { data: projects } = await supabase
        .from("projects")
        .select("owner_id")
        .in("owner_id", profiles.map(p => p.id));

      const counts = {};
      (projects || []).forEach(p => { counts[p.owner_id] = (counts[p.owner_id] || 0) + 1; });

      setUsers(profiles);
      setProjectCounts(counts);
      setLoading(false);
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (!openRoleId) return;
    const handler = () => setOpenRoleId(null);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [openRoleId]);

  const toggleActive = async (user) => {
    const newVal = !(user.is_active !== false);
    const { error } = await supabase.from("profiles").update({ is_active: newVal }).eq("id", user.id);
    if (error) { showToast("❌ " + error.message); return; }
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: newVal } : u));
  };

  const changeRole = async (user, newRole) => {
    setOpenRoleId(null);
    if (newRole === user.role) return;
    const { error } = await supabase.from("profiles").update({ role: newRole }).eq("id", user.id);
    if (error) { showToast("❌ " + error.message); return; }
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: newRole } : u));
    showToast(`✓ Rôle → ${ROLE_LABELS[newRole]}`);
  };

  const confirmDelete = async () => {
    const { error } = await supabase.from("profiles").delete().eq("id", deleteTarget.id);
    if (error) { showToast("❌ " + error.message); setDeleteTarget(null); return; }
    setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
    setDeleteTarget(null);
    showToast("✓ Compte supprimé");
  };

  const roleCounts = ROLES.reduce((acc, r) => { acc[r] = users.filter(u => u.role === r).length; return acc; }, {});

  const visible = users.filter(u => {
    if (filterRole !== "all" && u.role !== filterRole) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!(u.name || "").toLowerCase().includes(q) && !(u.email || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, fontWeight: 400 }}>Utilisateurs</h1>
        <p style={{ fontSize: 13, color: "#7A7468", marginTop: 4 }}>
          {loading ? "Chargement…" : `${users.length} compte${users.length !== 1 ? "s" : ""}`}
        </p>
      </div>

      {/* Toolbar: search + role filters */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 20 }}>
        <input
          placeholder="Rechercher par nom ou email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 260, fontSize: 13, padding: "7px 12px" }}
        />
        <div style={{ display: "flex", gap: 6 }}>
          {[["all", "Tous", users.length], ...ROLES.map(r => [r, ROLE_LABELS[r], roleCounts[r] || 0])].map(([val, label, count]) => {
            const active = filterRole === val;
            return (
              <button
                key={val}
                className="btn"
                onClick={() => setFilterRole(val)}
                style={{ padding: "5px 12px", borderRadius: 99, fontSize: 11, fontWeight: active ? 600 : 400, border: `1px solid ${active ? "#C9A96E60" : "#2A2924"}`, background: active ? "#C9A96E18" : "transparent", color: active ? "#C9A96E" : "#7A7468" }}
              >
                {label} <span style={{ opacity: .6 }}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ background: "#1A1916", border: "1px solid #2A2924", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #2A2924" }}>
              {["Nom", "Email", "Rôle", "Statut", "Projets", "Inscription", "Actions"].map((h, i) => (
                <th key={i} style={{ padding: "12px 16px", textAlign: i === 4 ? "center" : "left", fontSize: 10, color: "#7A7468", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 48, textAlign: "center" }}>
                <div className="spinner" style={{ margin: "0 auto" }} />
              </td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 48, textAlign: "center", color: "#7A7468", fontSize: 13 }}>
                {search ? "Aucun résultat" : "Aucun utilisateur"}
              </td></tr>
            ) : visible.map((user, i) => {
              const isActive = user.is_active !== false;
              const rc = ROLE_COLORS[user.role];
              return (
                <tr key={user.id} style={{ borderBottom: i < visible.length - 1 ? "1px solid #1F1E1A" : "none" }}>
                  {/* Nom */}
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, background: rc ? rc.bg : "#252420", border: `1px solid ${rc ? rc.border : "#2A2924"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: rc ? rc.text : "#7A7468" }}>
                        {rc ? rc.icon : "?"}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{user.name || "—"}</span>
                    </div>
                  </td>
                  {/* Email */}
                  <td style={{ padding: "13px 16px", fontSize: 13, color: "#7A7468" }}>{user.email || "—"}</td>
                  {/* Rôle */}
                  <td style={{ padding: "13px 16px" }}><RoleBadge role={user.role} /></td>
                  {/* Statut */}
                  <td style={{ padding: "13px 16px" }}><StatusBadge active={isActive} /></td>
                  {/* Projets */}
                  <td style={{ padding: "13px 16px", textAlign: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: projectCounts[user.id] > 0 ? "#C9A96E" : "#7A7468" }}>
                      {projectCounts[user.id] || 0}
                    </span>
                  </td>
                  {/* Inscription */}
                  <td style={{ padding: "13px 16px", fontSize: 12, color: "#7A7468", whiteSpace: "nowrap" }}>{fmtDate(user.created_at)}</td>
                  {/* Actions */}
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <button
                        className="btn"
                        onClick={() => toggleActive(user)}
                        style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 500, whiteSpace: "nowrap", border: `1px solid ${isActive ? "#FC818140" : "#A8C97E40"}`, color: isActive ? "#FC8181" : "#A8C97E", background: isActive ? "#FC818112" : "#A8C97E12" }}
                      >
                        {isActive ? "Désactiver" : "Activer"}
                      </button>
                      <RoleDropdown
                        currentRole={user.role}
                        open={openRoleId === user.id}
                        onToggle={() => setOpenRoleId(openRoleId === user.id ? null : user.id)}
                        onSelect={(role) => changeRole(user, role)}
                      />
                      <button
                        className="btn"
                        onClick={() => setDeleteTarget(user)}
                        style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #FC818140", fontSize: 11, color: "#FC8181", background: "#FC818112", fontWeight: 500 }}
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Supprimer ce compte ?"
          message={`Le compte de ${deleteTarget.name || deleteTarget.email || "cet utilisateur"} sera définitivement supprimé.`}
          confirmLabel="Supprimer"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
